import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import SectionWrapper from "../components/SectionWrapper";
import StepProgress from "../components/ui/StepProgress";
import Modal from "../components/ui/Modal";
import {
  consultantDoctors,
  delhiNcrPincodes,
} from "../data/healthcareContent";
import { bookAppointment, getBookings } from "../api/bookingApi";

const BookDoctorConsultantPage = () => {
  const location = useLocation();
  const initialBookingType = location.state?.bookingType || "";
  const [stepIndex, setStepIndex] = useState(0);
  const [successOpen, setSuccessOpen] = useState(false);
  const [, setBookings] = useState([]);

  const [formData, setFormData] = useState({
    bookingType: initialBookingType,
    doctorId: "",
    department: "",
    date: "",
    slot: "",
    consultationMode: "Online",
    specialist: "",
    addressLine: "",
    locality: "",
    city: "",
    pincode: "",
    visitTiming: "",
    visitPrice: "1100" ?? 0,
    emergency: false,
    patientName: "",
    patientAge: "",
    gender: "",
    mobile: "",
    notes: "",
  });

  const selectedDoctor = useMemo(
    () => consultantDoctors.find((doctor) => doctor.id === formData.doctorId),
    [formData.doctorId]
  );

  const stepLabels = useMemo(() => {
    if (formData.bookingType === "home") return ["Schedule", "Patient", "Confirm"];
    return ["Patient", "Confirm"];
  }, [formData.bookingType]);

  const currentStepKey = stepLabels[stepIndex] || "Patient";

  useEffect(() => {
    setStepIndex((prev) => Math.min(prev, Math.max(stepLabels.length - 1, 0)));
  }, [stepLabels.length]);

  const isNcrPincode = formData.pincode
    ? delhiNcrPincodes.includes(formData.pincode)
    : true;

  const loadBookings = async () => {
    const response = await getBookings();

    if (response?.Success !== false) {
      const items = Array.isArray(response)
        ? response
        : response?.Data ?? response?.data ?? [];
      setBookings(items);
    }
  };

  useEffect(() => {
    const fetchBookings = async () => {
      await loadBookings();
    };

    fetchBookings();
  }, []);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validateStep = () => {
    if (!formData.bookingType) return false;

    if (currentStepKey === "Schedule" && formData.bookingType === "home") {
      return Boolean(
        formData.addressLine &&
        formData.locality &&
        formData.city &&
        formData.pincode &&
        formData.visitTiming &&
        // formData.specialist &&
        isNcrPincode
      );
    }

    if (currentStepKey === "Patient") {
      return Boolean(formData.patientName && formData.patientAge && formData.mobile && formData.visitPrice);
    }

    return true;
  };

  const handleNext = () => {
    if (!validateStep()) {
      alert("Please complete required details for this step.");
      return;
    }
    setStepIndex((prev) => Math.min(prev + 1, stepLabels.length - 1));
  };

  const handleBack = () => {
    setStepIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleConfirm = async () => {
    if (!validateStep()) {
      alert("Please review the booking details before confirming.");
      return;
    }

    const response = await bookAppointment(formData);

    if (response?.Success !== false) {
      await loadBookings();
      setSuccessOpen(true);
      setStepIndex(0);
      setFormData({
        bookingType: formData.bookingType,
        doctorId: "",
        department: "",
        date: "",
        slot: "",
        consultationMode: "Online",
        // specialist: "",
        addressLine: "",
        locality: "",
        city: "",
        pincode: "",
        visitTiming: "",
        emergency: false,
        patientName: "",
        patientAge: "",
        gender: "",
        mobile: "",
        notes: "",
      });
    } else {
      alert(response?.Message || "Unable to submit booking right now.");
    }
  };

  return (
    <main className="mt-20 bg-cream pb-24 md:pb-16">
      <SectionWrapper className="py-10">
        <div className="rounded-3xl bg-gradient-to-r from-maroon to-[#2f5475] p-8 text-white shadow-xl">
          <p className="mb-3 text-sm uppercase tracking-[0.22em] text-white/80">Appointment Journey</p>
          <h1 className="mb-3 text-3xl font-bold md:text-4xl">Book Doctor Consultation</h1>
          <p className="max-w-3xl text-white/85">
            Multi-step booking flow for Daily Consultation and Home Visit, designed for quick, reliable family care planning.
          </p>
        </div>
      </SectionWrapper>

      <SectionWrapper className="pt-0 pb-2 md:pb-3">
        <StepProgress steps={stepLabels} currentStep={stepIndex + 1} />
      </SectionWrapper>

      <SectionWrapper className="pt-2 md:pt-3">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStepKey}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.25 }}
            className="rounded-2xl bg-white p-6 shadow-md md:p-8"
          >
            {currentStepKey === "Schedule" && formData.bookingType === "home" ? (
              <div>
                <h2 className="text-2xl font-bold text-maroon">Home Visit Scheduling</h2>
                <p className="mt-3 inline-flex rounded-full bg-rose-100 px-4 py-2 text-sm font-semibold text-rose-700">
                  Available Only in Delhi NCR
                </p>

                <div className="mt-6 grid gap-4 md:grid-cols-2">
                  <input
                    type="text"
                    name="addressLine"
                    value={formData.addressLine}
                    onChange={handleChange}
                    placeholder="Address Line"
                    className="rounded-xl border border-gray-300 px-4 py-3"
                    required
                  />
                  <input
                    type="text"
                    name="locality"
                    value={formData.locality}
                    onChange={handleChange}
                    placeholder="Locality"
                    className="rounded-xl border border-gray-300 px-4 py-3"
                    required
                  />
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="City"
                    className="rounded-xl border border-gray-300 px-4 py-3"
                    required
                  />
                  <input
                    type="text"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleChange}
                    placeholder="Delhi NCR Pincode"
                    className="rounded-xl border border-gray-300 px-4 py-3"
                    required
                  />
                  {!isNcrPincode ? (
                    <p className="md:col-span-2 text-sm text-rose-600">
                      Service is currently limited to Delhi NCR pincodes.
                    </p>
                  ) : null}

                  <select
                    name="visitTiming"
                    value={formData.visitTiming}
                    onChange={handleChange}
                    className="rounded-xl border border-gray-300 px-4 py-3"
                    required
                  >
                    <option value="">Preferred Visit Timing</option>
                    <option value="Morning (8 AM - 11 AM)">Morning (8 AM - 11 AM)</option>
                    <option value="Afternoon (12 PM - 4 PM)">Afternoon (12 PM - 4 PM)</option>
                    <option value="Evening (5 PM - 8 PM)">Evening (5 PM - 8 PM)</option>
                  </select>

                  {/* <select
                    name="specialist"
                    value={formData.specialist}
                    onChange={handleChange}
                    className="rounded-xl border border-gray-300 px-4 py-3"
                    required
                  >
                    <option value="">Specialist / Doctor Type</option>
                    {departments.map((department) => (
                      <option key={department} value={department}>
                        {department}
                      </option>
                    ))}
                  </select> */}
                </div>

                <label className="mt-4 flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    name="emergency"
                    checked={formData.emergency}
                    onChange={handleChange}
                  />
                  Emergency request
                </label>

                <div className="mt-5 grid gap-4 md:grid-cols-2">
                  <div className="rounded-2xl border border-gray-200 bg-cream p-4">
                    <h3 className="text-lg font-semibold text-maroon">Visit Charges</h3>
                    <p className="mt-2 text-sm text-gray-700">Consultation + care coordination starting from</p>
                    <p className="mt-2 text-2xl font-bold text-maroon">Rs. 5000</p>
                  </div>
                  <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-sky-50 to-white p-4">
                    <h3 className="text-lg font-semibold text-maroon">Location Preview (Demo)</h3>
                    <div className="mt-3 h-24 rounded-xl bg-[radial-gradient(circle_at_20%_20%,#93c5fd_2px,transparent_3px)] bg-[length:24px_24px] relative">
                      <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-maroon px-2 py-1 text-xs text-white">
                        Visit Pin
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {currentStepKey === "Patient" ? (
              <div>
                <h2 className="text-2xl font-bold text-maroon">Patient Details</h2>
                <div className="mt-6 grid gap-4 md:grid-cols-2">
                  <input
                    type="text"
                    name="patientName"
                    value={formData.patientName}
                    onChange={handleChange}
                    placeholder="Patient Full Name"
                    className="rounded-xl border border-gray-300 px-4 py-3"
                    required
                  />
                  <input
                    type="number"
                    name="patientAge"
                    value={formData.patientAge}
                    onChange={handleChange}
                    placeholder="Age"
                    className="rounded-xl border border-gray-300 px-4 py-3"
                    required
                  />
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    className="rounded-xl border border-gray-300 px-4 py-3"
                  >
                    <option value="">Gender</option>
                    <option value="Female">Female</option>
                    <option value="Male">Male</option>
                    <option value="Other">Other</option>
                  </select>
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="Mobile Number"
                    className="rounded-xl border border-gray-300 px-4 py-3"
                    required
                  />
                  {/* <select
                    name="visitPrice"
                    value={formData.visitPrice}
                    onChange={handleChange}
                    className="rounded-xl border border-gray-300 px-4 py-3"
                    required
                  >
                    <option value="">Select Visit Charges</option>
                    <option value="500">₹500</option>
                    <option value="1100">₹1100</option>
                  </select> */}
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Symptoms / Notes"
                    className="rounded-xl border border-gray-300 px-4 py-3 md:col-span-2"
                  />
                  <div className="rounded-2xl border border-gray-200 bg-cream p-4">
                    <h3 className="text-lg font-semibold text-maroon">Visit Charges</h3>
                    <p className="mt-2 text-sm text-gray-700">Consultation + care coordination starting from</p>
                    <p className="mt-2 text-2xl font-bold text-maroon">Rs. 1100</p>
                  </div>
                </div>
              </div>
            ) : null}

            {currentStepKey === "Confirm" ? (
              <div>
                <h2 className="text-2xl font-bold text-maroon">Review & Confirm</h2>
                <div className="mt-6 grid gap-4 md:grid-cols-2">
                  <div className="rounded-2xl border border-gray-200 bg-cream p-4">
                    <h3 className="font-semibold text-maroon">Booking Summary</h3>
                    <ul className="mt-3 space-y-1 text-sm text-gray-700">
                      <li>Type: {formData.bookingType === "daily" ? "Daily Consultant" : "Home Visit"}</li>
                      <li>Doctor: {selectedDoctor?.name || formData.specialist || "-"}</li>
                      <li>Department: {formData.department || formData.specialist || "-"}</li>
                      <li>Date: {formData.date || "To be assigned"}</li>
                      <li>Slot: {formData.slot || formData.visitTiming || "-"}</li>
                      <li>Mode: {formData.consultationMode}</li>
                    </ul>
                  </div>

                  <div className="rounded-2xl border border-gray-200 bg-cream p-4">
                    <h3 className="font-semibold text-maroon">Patient Summary</h3>
                    <ul className="mt-3 space-y-1 text-sm text-gray-700">
                      <li>Name: {formData.patientName}</li>
                      <li>Age: {formData.patientAge}</li>
                      <li>Gender: {formData.gender || "-"}</li>
                      <li>Mobile: {formData.mobile}</li>
                      {formData.bookingType === "home" ? (
                        <li>
                          Address: {formData.addressLine}, {formData.locality}, {formData.city} - {formData.pincode}
                        </li>
                      ) : null}
                    </ul>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="mt-8 hidden items-center justify-between md:flex">
              <button
                type="button"
                onClick={handleBack}
                disabled={stepIndex === 0}
                className="rounded-full border border-maroon px-5 py-2 text-sm font-semibold text-maroon disabled:opacity-40"
              >
                Back
              </button>

              {stepIndex < stepLabels.length - 1 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="rounded-full bg-maroon px-6 py-2 text-sm font-semibold text-white"
                >
                  Next Step
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleConfirm}
                  className="rounded-full bg-maroon px-6 py-2 text-sm font-semibold text-white"
                >
                  Confirm Booking
                </button>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </SectionWrapper>

      <div className="fixed inset-x-0 bottom-0 z-[55] border-t border-gray-200 bg-white/95 p-3 shadow-lg backdrop-blur md:hidden">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3">
          <button
            type="button"
            onClick={handleBack}
            disabled={stepIndex === 0}
            className="flex-1 rounded-full border border-maroon px-4 py-3 text-sm font-semibold text-maroon disabled:opacity-40"
          >
            Back
          </button>
          {stepIndex < stepLabels.length - 1 ? (
            <button
              type="button"
              onClick={handleNext}
              className="flex-1 rounded-full bg-maroon px-4 py-3 text-sm font-semibold text-white"
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              onClick={handleConfirm}
              className="flex-1 rounded-full bg-maroon px-4 py-3 text-sm font-semibold text-white"
            >
              Confirm
            </button>
          )}
        </div>
      </div>

      <Modal
        isOpen={successOpen}
        onClose={() => setSuccessOpen(false)}
        title="Appointment Request Submitted"
        maxWidth="max-w-md"
      >
        <p className="text-sm leading-relaxed text-gray-700">
          Thank you. Your consultation request has been captured successfully. Our care coordinator will confirm the doctor allocation and slot shortly.
        </p>
      </Modal>
    </main>
  );
};

export default BookDoctorConsultantPage;
